<template>
  <section>
    <div class="listview__head">
      <div>
        <div class="c-lbl">Clientes y proveedores</div>
        <div class="listview__desc">{{ pagination.total || 0 }} registros</div>
      </div>
      <button class="btn btn-p" @click="openNew"><span>+</span> Nuevo</button>
    </div>

    <div class="listview__filters">
      <input class="fi" v-model="filters.q" @input="debouncedFetch" placeholder="Buscar por nombre, CUIT o email..." />
      <select class="fi" v-model="filters.tipo" @change="fetch" style="max-width:180px">
        <option value="">Todos los tipos</option>
        <option value="cliente">Cliente</option>
        <option value="cliente_i">Cliente I</option>
        <option value="proveedor">Proveedor</option>
        <option value="ambos">Ambos</option>
      </select>
    </div>

    <div class="card">
      <div class="tbl-wrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>Nombre</th><th>Tipo</th><th>CUIT</th><th>Email</th><th>Teléfono</th><th style="width:120px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="6" class="tbl-empty">Cargando...</td></tr>
            <tr v-else-if="!items.length"><td colspan="6" class="tbl-empty">Sin registros. Probá crear uno nuevo.</td></tr>
            <tr v-for="c in items" :key="c.id">
              <td><strong style="color:var(--tx-hi)">{{ c.nombre }}</strong></td>
              <td><span class="pill" :class="tipoPill(c.tipo)">{{ c.tipo }}</span></td>
              <td class="mono">{{ c.cuit ? fmtCuit(c.cuit) : '—' }}</td>
              <td>{{ c.email || '—' }}</td>
              <td>{{ c.telefono || '—' }}</td>
              <td class="tbl-actions">
                <button class="btn-ico" @click="openEdit(c)" title="Editar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button class="btn-ico" :class="c.activo === false ? 'btn-ico--success' : 'btn-ico--danger'" @click="toggleActivo(c)" :title="c.activo === false ? 'Activar' : 'Desactivar'">
                  <svg v-if="c.activo !== false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="listview__pag" v-if="pagination.last_page > 1">
        <button class="btn btn-g btn-sm" :disabled="pagination.current_page <= 1" @click="go(pagination.current_page - 1)">‹ Anterior</button>
        <span>Página {{ pagination.current_page }} / {{ pagination.last_page }}</span>
        <button class="btn btn-g btn-sm" :disabled="pagination.current_page >= pagination.last_page" @click="go(pagination.current_page + 1)">Siguiente ›</button>
      </div>
    </div>

    <!-- Modal crear/editar -->
    <Modal v-model="modal.open" :title="modal.id ? 'Editar cliente' : 'Nuevo cliente'">
      <form @submit.prevent="save" class="form-grid" id="cliente-form">
        <div>
          <label class="fl">CUIT <span class="req">*</span></label>
          <CuitInput v-model="modal.data.cuit" @arca-data="onArcaData" />
          <div v-if="modal.arcaInfo" class="form-arca-info">
            <strong>ARCA:</strong> {{ modal.arcaInfo }}
          </div>
        </div>
        <div class="form-row form-row--2">
          <div><label class="fl">Nombre / Razón social <span class="req">*</span></label>
            <input class="fi" v-model="modal.data.nombre" required maxlength="160" /></div>
          <div><label class="fl">Tipo <span class="req">*</span></label>
            <select class="fi" v-model="modal.data.tipo">
              <option value="cliente">Cliente</option>
              <option value="cliente_i">Cliente I</option>
              <option value="proveedor">Proveedor</option>
              <option value="ambos">Ambos</option>
            </select></div>
        </div>
        <div class="form-row form-row--2">
          <div><label class="fl">Teléfono</label><input class="fi" v-model="modal.data.telefono" maxlength="40" /></div>
          <div><label class="fl">Email</label><input class="fi" v-model="modal.data.email" type="email" maxlength="160" /></div>
        </div>
        <div><label class="fl">Dirección</label><input class="fi" v-model="modal.data.direccion" maxlength="200" /></div>
        <div><label class="fl">Notas</label><textarea class="fi" v-model="modal.data.notas" rows="2"></textarea></div>

        <!-- Recordatorios de pago automáticos (cada N días hábiles mientras haya facturas impagas) -->
        <div style="margin-top:8px;display:flex;align-items:center;gap:12px;flex-wrap:wrap">
          <label class="fl" style="display:flex;align-items:center;gap:8px;margin:0">
            <input type="checkbox" v-model="modal.data.recordatorios_pago" style="accent-color:var(--accent)" />
            Recordatorio de pago automático cada
          </label>
          <input class="fi" v-model.number="modal.data.recordatorios_cada_habiles" type="number" min="1" max="120" step="1" placeholder="10" style="width:64px;text-align:center" />
          <span class="fl" style="margin:0">días hábiles mientras tenga facturas impagas</span>
          <button v-if="modal.id" type="button" class="btn btn-g btn-sm" :disabled="recordatorio.busy" @click="enviarRecordatorio">Enviar recordatorio ahora</button>
          <span v-if="recordatorio.info" class="xs" style="color:var(--tx-md)">{{ recordatorio.info }}</span>
        </div>

        <!-- Facturación mensual — una tab por emisor. Cada emisor puede tener config independiente. -->
        <div class="form-section" v-if="modal.id">
          <div class="form-section__title">Facturación mensual (por emisor)</div>

          <div v-if="emisores.length === 0" class="xs" style="color:var(--tx-md)">
            No tenés emisores cargados todavía. Primero creá tus emisores desde /perfil.
          </div>

          <div v-else style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px">
            <button
              v-for="e in emisores"
              :key="e.id"
              type="button"
              class="btn btn-sm"
              :class="modal.emisorTab === e.cuit ? 'btn-p' : 'btn-g'"
              @click="modal.emisorTab = e.cuit"
              style="display:flex;align-items:center;gap:6px"
            >
              <span
                :style="{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: facturacionActiva(e.cuit) ? '#16a34a' : '#cbd5e0'
                }"
              ></span>
              {{ e.razon_social }}
            </button>
          </div>

          <template v-if="modal.emisorTab && facturacionActual">
            <div class="form-row form-row--2">
              <div>
                <label class="fl">Monto mensual USD</label>
                <input class="fi" v-model.number="facturacionActual.monto_usd" type="number" step="0.01" min="0" />
              </div>
              <div>
                <label class="fl">Cotización BNA</label>
                <select class="fi" v-model="facturacionActual.cotizacion_tipo">
                  <option value="compra">Compra</option>
                  <option value="venta">Venta</option>
                </select>
              </div>
            </div>
            <div class="form-row form-row--2">
              <div>
                <label class="fl">Descripción factura</label>
                <input class="fi" v-model="facturacionActual.descripcion" maxlength="255" placeholder="Servicio de..." />
              </div>
              <div>
                <label class="fl">Código servicio</label>
                <input class="fi" v-model="facturacionActual.codigo_servicio" maxlength="10" placeholder="STC" />
              </div>
            </div>
            <div class="form-row form-row--2">
              <div>
                <label class="fl">Punto de venta WS</label>
                <input class="fi" v-model.number="facturacionActual.pto_vta" type="number" min="1" />
              </div>
              <div>
                <label class="fl">Tipo comprobante</label>
                <select class="fi" v-model.number="facturacionActual.cbte_tipo">
                  <option :value="11">Factura C (11)</option>
                  <option :value="1">Factura A (1)</option>
                  <option :value="6">Factura B (6)</option>
                </select>
              </div>
            </div>
            <div>
              <label class="fl">Condición IVA receptor</label>
              <select class="fi" v-model.number="facturacionActual.condicion_iva_receptor">
                <option :value="null">— Sin definir —</option>
                <option :value="1">IVA Responsable Inscripto</option>
                <option :value="4">IVA Sujeto Exento</option>
                <option :value="5">Consumidor Final</option>
                <option :value="6">Responsable Monotributo</option>
              </select>
            </div>
            <div>
              <label class="fl">Emails envío facturas</label>
              <input class="fi" v-model="facturacionActual.emails_factura" maxlength="500" placeholder="correo1@empresa.com, correo2@empresa.com" />
            </div>
            <div>
              <label class="fl">Emails envío recibos</label>
              <input class="fi" v-model="facturacionActual.emails_recibo" maxlength="500" placeholder="correo1@empresa.com, correo2@empresa.com" />
            </div>
            <div>
              <label class="fl" style="display:flex;align-items:center;gap:8px">
                <input type="checkbox" v-model="facturacionActual.activa" style="accent-color:var(--accent)" />
                Facturación mensual activa por {{ nombreEmisorActual }}
              </label>
            </div>

            <!-- Nota de gastos a recuperar (se imprime en el PDF de la factura) -->
            <div style="margin-top:12px;padding:12px;border:1px solid var(--brd,#dbe3ea);border-radius:8px;background:rgba(21,96,189,0.04)">
              <label class="fl" style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
                <input type="checkbox" v-model="facturacionActual.nota_gastos_activa" style="accent-color:var(--accent)" />
                Incluir nota de gastos a recuperar en las facturas
              </label>
              <template v-if="facturacionActual.nota_gastos_activa">
                <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:13px;">
                  <template v-if="recupero.loading">Leyendo mhlm.ar/gastos…</template>
                  <template v-else-if="recupero.data && recupero.data.ok">
                    <span>Pendiente hoy: <b>US$ {{ fmtUsd(recupero.data.pendiente_usd) }}</b></span>
                    <span class="xs" style="color:var(--tx-md)">{{ recupero.data.n_gastos }} gastos · reintegrado US$ {{ fmtUsd(recupero.data.reintegrado_usd) }} · planilla del {{ fmtFechaHora(recupero.data.generado) }} · leído {{ recupero.data.fuente === 'copia' ? 'de la última copia' : 'en línea' }}</span>
                    <a href="https://mhlm.ar/gastos" target="_blank" rel="noopener" class="xs">mhlm.ar/gastos</a>
                  </template>
                  <span v-else class="form-error" style="margin:0">{{ recupero.error || 'No se pudo leer mhlm.ar/gastos.' }}</span>
                  <button type="button" class="btn btn-g btn-sm" :disabled="recupero.loading" @click="cargarRecupero(true)">Actualizar</button>
                </div>
                <div v-if="recupero.data && recupero.data.aviso" class="xs" style="margin-top:4px;color:#b26a00">{{ recupero.data.aviso }}</div>
                <div style="margin-top:8px">
                  <label class="fl">Texto de la nota</label>
                  <textarea class="fi" v-model="facturacionActual.nota_gastos_texto" rows="3" maxlength="500"
                            :placeholder="notaTextoDefault"></textarea>
                  <div class="xs" style="margin-top:4px;color:var(--tx-md)">
                    Se reemplazan al emitir: {{ '{importe}' }} (US$), {{ '{importe_ars}' }} y {{ '{cotizacion}' }} (al tipo de cambio BNA de la fecha de la factura), {{ '{fecha}' }} (fin del período), {{ '{fecha_factura}' }}. Vacío = usa el texto de arriba.
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>

        <div v-if="modal.error" class="form-error">{{ modal.error }}</div>
      </form>
      <template #footer>
        <button class="btn btn-g" @click="modal.open = false">Cancelar</button>
        <button class="btn btn-p" form="cliente-form" type="submit" :disabled="modal.saving">
          {{ modal.saving ? 'Guardando...' : (modal.id ? 'Guardar cambios' : 'Crear cliente') }}
        </button>
      </template>
    </Modal>

  </section>
</template>

<script>
import api from '../services/api';
import Modal from '../components/Modal.vue';
import CuitInput from '../components/CuitInput.vue';
import { format as fmtCuit } from '../utils/cuit';

function empty() {
  return {
    nombre: '', tipo: 'cliente', cuit: '', email: '', telefono: '', direccion: '', notas: '',
    recordatorios_pago: false,
    recordatorios_cada_habiles: null, // vacío = 10 (config general)
    facturaciones: [], // array de { cuit_emisor, activa, monto_usd, ... }
  };
}

const NOTA_TEXTO_DEFAULT =
  'GASTOS A RECUPERAR — Gastos de infraestructura pendientes de reintegro '
  + 'al {fecha}: US$ {importe}. Equivalen a $ {importe_ars} al tipo de cambio BNA '
  + '{tipo_cotizacion} del {fecha_factura} ($ {cotizacion} por dólar). Comprobantes en mhlm.ar/gastos. '
  + 'Importe informativo; no integra el total de esta factura.';

function emptyFacturacion(cuitEmisor, ptoVtaDefault) {
  return {
    cuit_emisor: cuitEmisor,
    activa: false,
    monto_usd: null,
    descripcion: '',
    codigo_servicio: '',
    cotizacion_tipo: 'compra',
    pto_vta: ptoVtaDefault || null,
    cbte_tipo: 11,
    condicion_iva_receptor: null,
    emails_factura: '',
    emails_recibo: '',
    nota_gastos_activa: false,
    nota_gastos_usd: null,
    nota_gastos_texto: '',
  };
}

function emptyModal() {
  return { open: false, id: null, data: empty(), saving: false, error: '', arcaInfo: '', emisorTab: '' };
}

function emptyRecupero() {
  return { loading: false, data: null, error: '', key: '' };
}

export default {
  name: 'ClientesView',
  components: { Modal, CuitInput },
  data() {
    return {
      items: [], loading: false,
      pagination: { current_page: 1, last_page: 1, total: 0 },
      filters: { q: '', tipo: '', page: 1 },
      modal:   emptyModal(),
      recupero: emptyRecupero(),
      recordatorio: { busy: false, info: '' },
      emisores: [],
      fmtCuit,
      notaTextoDefault: NOTA_TEXTO_DEFAULT,
      _debounce: null
    };
  },
  async mounted() { await Promise.all([this.fetch(), this.loadEmisores()]); },
  computed: {
    // Objeto de facturación del emisor actualmente seleccionado en la tab.
    // Si no existe en el array del cliente, lo creo al vuelo cuando el
    // usuario selecciona la tab (para que v-model tenga a qué bindear).
    facturacionActual() {
      if (! this.modal.emisorTab) return null;
      let f = this.modal.data.facturaciones.find(x => x.cuit_emisor === this.modal.emisorTab);
      if (! f) {
        const e = this.emisores.find(em => em.cuit === this.modal.emisorTab);
        f = emptyFacturacion(this.modal.emisorTab, e?.pto_vta_ws);
        this.modal.data.facturaciones.push(f);
      }
      return f;
    },
    nombreEmisorActual() {
      const e = this.emisores.find(em => em.cuit === this.modal.emisorTab);
      return e?.razon_social || '';
    },
  },
  watch: {
    // Al abrir la pestaña de un emisor con la nota activa, leer el pendiente en línea.
    'modal.emisorTab'() { this.cargarRecupero(); },
    'modal.open'(v) {
      if (v) { this.cargarRecupero(); this.cargarRecordatorio(); return; }
      this._recuperoSeq = (this._recuperoSeq || 0) + 1; // invalida lo que esté en vuelo
      this.recupero = emptyRecupero();
      this.recordatorio = { busy: false, info: '' };
    },
    'facturacionActual.nota_gastos_activa'(v) { if (v) this.cargarRecupero(); },
  },
  methods: {
    // Último recordatorio de pago enviado a este cliente (para mostrarlo al lado del switch).
    async cargarRecordatorio() {
      if (!this.modal.id) return;
      try {
        const { data } = await api.get('/recordatorios', { params: { cliente_id: this.modal.id } });
        const u = (data.data || [])[0];
        this.recordatorio.info = u
          ? `Último recordatorio: ${this.fmtFechaHora(u.enviado_at)} a ${u.destinatarios}${u.ok ? '' : ' (falló: ' + (u.error || '') + ')'}`
          : '';
      } catch (e) { /* sin info */ }
    },
    async enviarRecordatorio() {
      if (!this.modal.id) return;
      const ok = await this.$msg.confirm('Enviar ahora el recordatorio de pago con todas las facturas impagas de este cliente?', 'Recordatorio de pago');
      if (!ok) return;
      this.recordatorio.busy = true;
      try {
        const { data } = await api.post('/recordatorios/correr', { cliente_id: this.modal.id, forzar: 1 });
        const env = data.enviados || [], om = data.omitidos || [];
        if (env.length) this.$msg.success(`Recordatorio enviado a ${env.map(r => (r.destinatarios || []).join(', ')).join(' / ')}`);
        else this.$msg.info(om.length ? om.map(r => r.motivo).join(' · ') : 'Este cliente no tiene facturas impagas.');
        await this.cargarRecordatorio();
      } catch (e) {
        this.$msg.error(e?.response?.data?.message || 'No se pudo enviar el recordatorio.');
      } finally { this.recordatorio.busy = false; }
    },
    fmtUsd(n) { return Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    fmtFechaHora(iso) { if (!iso) return '—'; const d = new Date(iso); return isNaN(d) ? String(iso) : d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); },
    async cargarRecupero(forzar = false) {
      // Solo para configs con la nota activa: la ficha de otros clientes no consulta nada.
      if (!this.modal.open || !this.modal.emisorTab || !this.facturacionActual?.nota_gastos_activa) return;
      const key = `${this.modal.id || 'nuevo'}|${this.modal.emisorTab}`;
      if (!forzar && this.recupero.key === key && (this.recupero.data || this.recupero.loading)) return;
      const seq = (this._recuperoSeq = (this._recuperoSeq || 0) + 1);
      this.recupero = { loading: true, data: null, error: '', key };
      let next;
      try {
        const params = { cuit_emisor: this.modal.emisorTab };
        if (this.modal.id) params.cliente_id = this.modal.id;
        if (forzar) params.forzar = 1;
        const { data } = await api.get('/gastos-recupero', { params });
        next = { loading: false, data, error: data.ok ? '' : (data.aviso || data.error || 'No se pudo leer mhlm.ar/gastos.'), key };
      } catch (e) {
        next = { loading: false, data: null, error: e?.response?.data?.message || 'No se pudo leer mhlm.ar/gastos.', key };
      }
      // Descartar respuestas viejas (se cambió de emisor o se cerró el modal mientras cargaba).
      if (seq !== this._recuperoSeq) return;
      this.recupero = next;
    },
    debouncedFetch() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.fetch(), 250);
    },
    async fetch() {
      this.loading = true;
      try {
        const { data } = await api.get('/clientes', { params: { ...this.filters, per_page: 25 } });
        const p = data.data;
        this.items = p.data;
        this.pagination = { current_page: p.current_page, last_page: p.last_page, total: p.total };
      } catch (e) {
        if (!e?.isAuthExpired) console.warn('[Clientes]', e);
      } finally { this.loading = false; }
    },
    go(page) { this.filters.page = page; this.fetch(); },
    openNew()  {
      const m = { ...emptyModal(), open: true };
      m.emisorTab = this.emisores[0]?.cuit || '';
      this.modal = m;
    },
    openEdit(c){
      const data = { ...empty(), ...c };
      data.facturaciones = Array.isArray(c.facturaciones) ? c.facturaciones.map(f => ({ ...f })) : [];
      const m = { ...emptyModal(), open: true, id: c.id, data };
      // Preselección de tab: emisor con facturación activa, sino primero disponible.
      m.emisorTab = data.facturaciones.find(f => f.activa)?.cuit_emisor
                  || this.emisores[0]?.cuit
                  || '';
      this.modal = m;
    },
    // Indica si el cliente tiene facturación activa por este emisor (usado para
    // el punto de color verde/gris en la tab).
    facturacionActiva(cuitEmisor) {
      const f = this.modal.data.facturaciones.find(x => x.cuit_emisor === cuitEmisor);
      return !!(f && f.activa);
    },
    onArcaData(payload) {
      if (payload.error) {
        this.modal.arcaInfo = `⚠ ${payload.error}`;
        return;
      }
      if (payload.nombre)    this.modal.data.nombre    = payload.nombre;
      if (payload.domicilio) this.modal.data.direccion  = payload.domicilio;
      if (payload.tipo)      this.modal.data.tipo       = payload.tipo === 'empresa' ? 'cliente' : 'cliente';
      this.modal.arcaInfo = `${payload.estado || 'OK'} — datos traídos del padrón AFIP`;
    },
    async save() {
      this.modal.saving = true; this.modal.error = '';
      try {
        // Solo mandamos las facturaciones "de verdad" — filtro las tabs que el
        // usuario abrió pero dejó todo vacío para no ensuciar la DB.
        const payload = { ...this.modal.data };
        payload.facturaciones = (this.modal.data.facturaciones || []).filter(f => {
          return f.cuit_emisor && (
            f.activa || f.monto_usd || f.descripcion || f.emails_factura || f.emails_recibo
            || f.nota_gastos_activa || f.nota_gastos_texto
          );
        });

        if (this.modal.id) await api.put(`/clientes/${this.modal.id}`, payload);
        else               await api.post('/clientes', payload);
        this.modal.open = false;
        await this.fetch();
      } catch (e) {
        this.modal.error = e?.response?.data?.message || Object.values(e?.response?.data?.errors ?? {}).flat()[0] || 'No se pudo guardar.';
      } finally { this.modal.saving = false; }
    },
    async toggleActivo(c) {
      try {
        await api.put(`/clientes/${c.id}`, { ...c, activo: c.activo === false ? true : false });
        await this.fetch();
      } catch (e) { console.warn('[toggleActivo]', e); }
    },
    async loadEmisores() {
      try {
        const { data } = await api.get('/emisores');
        this.emisores = data.data || [];
      } catch (e) { if (!e?.isAuthExpired) console.warn('[emisores]', e); }
    },
    tipoPill(t) { return ({ cliente: 'pill-b', cliente_i: 'pill-a', proveedor: 'pill-s', ambos: 'pill-g' })[t] || 'pill-b'; }
  }
};
</script>
